<script setup lang="tsx">
import type { RoleWithUsers } from '@/models/api-generated'
import PlusButton from '../widgets/PlusButton.vue'
import UserSelect from '../widgets/UserSelect.vue'
import PermissionEditor from './PermissionEditor.vue'
import IconButton from '../shared/IconButton.vue'

const props = defineProps({
	isChanged: Boolean,
	isDeleted: Boolean,
	isOdd: Boolean,
})
const emit = defineEmits(['delete', 'replaceResponsibilities'])
const model = defineModel<RoleWithUsers>({ required: true })

function rowClass() {
	return { 'is-changed': model.value.role.id === 0 || (props.isChanged && !props.isDeleted), 'is-deleted': props.isDeleted }
}

function toggleExpand(e: MouseEvent) {
	const target = e.target as HTMLElement
	if (target.closest('input, select, textarea, button, .bi-trash, .bi-arrow-repeat, .method-expand-icon')) return
	model.value.role.isExpanded = !model.value.role.isExpanded
}
</script>

<template>
	<div class="role row p-2" :class="[rowClass(), isOdd ? 'odd' : 'even']" @click="toggleExpand">
		<div class="col-4 d-flex align-items-center">
			<PlusButton v-if="model.role.id > 0" class="me-3" v-model="model.role.isExpanded"></PlusButton>
			<template v-if="!model.role.isExpanded || model.role.readOnly">
				<span class="text-break">{{ model.role.name }}</span>
			</template>
			<input
				v-else
				type="text"
				class="form-control"
				:class="{ 'is-invalid': !model.role.name }"
				:disabled="props.isDeleted"
				v-model="model.role.name"
				maxlength="50"
			/>
		</div>
		<div class="col-6 d-flex align-items-center">
			<template v-if="!model.role.isExpanded || model.role.readOnly">
				<span class="text-break">{{ model.role.description }}</span>
			</template>
			<input v-else type="text" class="form-control" :disabled="props.isDeleted" v-model="model.role.description" maxlength="200" />
		</div>
		<div class="col-1 d-flex align-items-end">
			{{ model.users.length }}
		</div>
		<div v-if="model.role.isExpanded && !model.role.readOnly" class="col-1 d-flex align-items-center flex-shrink-0">
			<IconButton
				:data-test="'delete-role' + model.role.id"
				class="bi bi-trash me-2"
				:class="props.isDeleted ? 'active' : ''"
				:title="$t('misc.delete')"
				@click="emit('delete')"
				size="fs-5"
			/>
			<IconButton
				:data-test="'replace-role-responsibilities' + model.role.id"
				class="bi bi-arrow-repeat"
				:title="$t('misc.replaceRoleResponsibilities')"
				@click="emit('replaceResponsibilities')"
				size="fs-5"
			/>
		</div>
	</div>

	<template v-if="model.role.isExpanded">
		<div class="permissions row mt-4" :class="rowClass()">
			<div class="col-4 me-3">
				<h6>{{ $t('user.label', 2) }}</h6>
			</div>
			<h6>{{ $t('permissions') }}</h6>
		</div>
		<div class="permissions row mb-5" :class="rowClass()">
			<div class="col-4 me-3">
				<div class="row py-3 rounded container-fluid border border-2">
					<div class="col-2">
						<i class="bi bi-person-add text-success fs-3"></i>
					</div>
					<div class="col">
						<UserSelect :multiple="true" v-model="model.users"></UserSelect>
					</div>
				</div>
			</div>
			<div class="col container-fluid rounded border border-2">
				<PermissionEditor class="my-3" v-model="model.role.permissions" :readOnly="model.role.readOnly"></PermissionEditor>
			</div>
		</div>
	</template>
</template>

<style lang="sass" scoped>
.role
  cursor: pointer
  border-top: 1px solid #dfe2e6

  &.odd
    background-color: var(--bs-body-bg)

  &.even
    background-color: var(--bs-secondary-bg)

.border-2
  border-color: #eee
.is-changed
  input:not(.is-invalid), select:not(.is-invalid)
    border: 1px solid blue
.role.is-changed
  background-color:  var(--bs-primary-bg-subtle)
.role.is-deleted
  background-color: rgba(255, 80, 80, .15)
.permissions.is-deleted
  opacity: .6
  pointer-events: none

span,
h6
  color: var(--bs-body-color)
</style>
